
import { ServiceLocator } from '../../../app/ServiceLocator';
import { IUpdateUserDisplayNameParams, UpdateUserDisplayNameRequest } from '../../../ts/ServerApi/requests/UpdateUserDisplayNameRequest';
import { WebHttpClientWithErrorToast, WebHttpClientWithErrorToastInstance } from '../../../httpClient/WebHttpClientWithErrorToast';
import { IUpdateUserEmailParams, UpdateUserEmailRequest } from '../../../ts/ServerApi/requests/UpdateUserEmailRequest';
import { IUpdateUserLanguageParams, UpdateUserLanguageRequest } from '../../../ts/ServerApi/requests/UpdateUserLanguageRequest';
import { IUpdateUserLocaleParams, UpdateUserLocaleRequest } from '../../../ts/ServerApi/requests/UpdateUserLocaleRequest';
import { IUpdateUserLocalTimeZoneDataParams, UpdateUserLocalTimeZoneDataRequest } from '../../../ts/ServerApi/requests/UpdateUserLocalTimeZoneDataRequest';
import { IUpdateUserStartPageParams, UpdateUserStartPageRequest } from '../../../ts/ServerApi/requests/UpdateUserStartPageRequest';
import { IUpdateSiteStartPageParams, UpdateSiteStartPageRequest } from '../../../ts/ServerApi/requests/UpdateSiteStartPageRequest';
import { SetShowWelcomeScreenRequest } from '../../../ts/ServerApi/requests/SetShowWelcomeScreenRequest';
import { SetShowMetricsBannerRequest } from '../../../ts/ServerApi/requests/SetShowMetricsBannerRequest';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '../../../ts/ServerApi/requests/GeneratePublicKeyRequest';
import { ChangeUserPasswordRequest } from '../../../ts/ServerApi/requests/ChangeUserPasswordRequest';
import { IUpdateNotificationSettingParams, UpdateNotificationSettingRequest } from '../../../ts/ServerApi/requests/UpdateNotificationSettingRequest';
import { IUpdateSuspendedSubscriptionSettingParams, UpdateSuspendedSubscriptionSettingRequest } from '../../../ts/ServerApi/requests/UpdateSuspendedSubscriptionSettingRequest';
import { IGetSuspendedSubscriptionSettingParams, GetSuspendedSubscriptionSettingRequest } from '../../../ts/ServerApi/requests/GetSuspendedSubscriptionSettingRequest';
import { IUpdateSuspendedDataAlertSettingParams, UpdateSuspendedDataAlertSettingRequest } from '../../../ts/ServerApi/requests/UpdateSuspendedDataAlertSettingRequest';
import { IGetSuspendedDataAlertSettingParams, GetSuspendedDataAlertSettingRequest } from '../../../ts/ServerApi/requests/GetSuspendedDataAlertSettingRequest';
import { IUpdateMarkAnimationSettingParams, UpdateMarkAnimationSettingRequest } from '../../../ts/ServerApi/requests/UpdateMarkAnimationSettingRequest';
import { IGetMarkAnimationSettingParams, GetMarkAnimationSettingRequest } from '../../../ts/ServerApi/requests/GetMarkAnimationSettingRequest';
import WebHttpClient from '../../../httpClient/WebHttpClient';


export namespace UserSettings {
  // Const
  const  ResetStartPage = 'unspecified';

  // Note: This is used as sentinel value. It should not be mutated.
  const  SameUserId: string = null;

  export function updateUserDisplayName(userId: string, displayName: string): ng.IPromise<any> {
    const params: IUpdateUserDisplayNameParams = {
      userId: userId,
      displayName: displayName
    };
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new UpdateUserDisplayNameRequest(params));
  }

  export function updateUserEmail(userId: string, email: string): ng.IPromise<any> {
    const params: IUpdateUserEmailParams = {
      userId: userId,
      email: email
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserEmailRequest(params));
  }

  export function updateUserEmailAndDelay(userId: string, email: string): ng.IPromise<any> {
      const params: IUpdateUserEmailParams = {
          userId: userId,
          email: email
      };
      return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new UpdateUserEmailRequest(params));
  }

  export function updateUserLanguage(userId: string, language: string): ng.IPromise<any> {
    const params: IUpdateUserLanguageParams = {
      userId: userId,
      language: language
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserLanguageRequest(params));
  }

  export function updateUserLocale(userId: string, locale: string): ng.IPromise<any> {
    const params: IUpdateUserLocaleParams = {
      userId: userId,
      locale: locale
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserLocaleRequest(params));
  }

  export function updateUserLocalTimeZoneData(offset: number, timeZoneId: string, isUserSetTimeZone: boolean): ng.IPromise<any> {
    const params: IUpdateUserLocalTimeZoneDataParams = {
      offset: offset,
      timeZoneId: timeZoneId,
      isUserSetTimeZone: isUserSetTimeZone
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserLocalTimeZoneDataRequest(params));
  }

  export function updateUserStartPage(userId: string, startPage: string): ng.IPromise<any> {
    const params: IUpdateUserStartPageParams = {
      startPage: startPage
    };

    if (userId !== SameUserId) {
      params.userId = userId;
    }
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserStartPageRequest(params));
  }

  export function updateCurrentUsersStartPage(startPage: string): ng.IPromise<any> {
    const params: IUpdateUserStartPageParams = {
      startPage: startPage
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserStartPageRequest(params));
  }

  export function updateSiteStartPage(startPage: string): ng.IPromise<any> {
    const params: IUpdateSiteStartPageParams = {
      startPage: startPage
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateSiteStartPageRequest(params));
  }

  export function updateWelcomeChannelEnabled(showWelcomeScreen: boolean): ng.IPromise<any> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new SetShowWelcomeScreenRequest({ showWelcomeScreen }));
  }

  export function updateShowMetricsBanner(showMetricsBanner: boolean): ng.IPromise<any> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new SetShowMetricsBannerRequest({ showMetricsBanner }));
  }

  export function resetUserStartPage(userId: string): ng.IPromise<any> {
    return updateUserStartPage(userId, ResetStartPage);
  }

  export function changeUserPassword(
    userId: string,
    newPassword: string,
    oldPassword?: string): Promise<IResult> {

    return WebHttpClient.sendRequest(new GeneratePublicKeyRequest()).
      then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
        const key = generatePublicKeyResult.key;
        const keyId = generatePublicKeyResult.keyId;

        // server admin does not need to enter old password to change a user's password
        let oldEncryptedPassword: string;
        if (oldPassword && oldPassword.length > 0) {
          oldEncryptedPassword = rsa.encrypt(oldPassword, key);
        }

        const newEncryptedPassword = rsa.encrypt(newPassword, key);

        const changePasswordRequest = new ChangeUserPasswordRequest({
          userId: userId,
          oldEncryptedPassword: oldEncryptedPassword,
          newEncryptedPassword: newEncryptedPassword,
          keyId: keyId
        });
        return WebHttpClientWithErrorToastInstance.sendRequest(changePasswordRequest);
      });
  }

  export function updateNotificationSetting(userId: string, enabled: boolean): ng.IPromise<any> {
    const params: IUpdateNotificationSettingParams = {
      userId: userId,
      enabled: enabled
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateNotificationSettingRequest(params));
  }

  export function updateSuspendedSubscriptionSetting(user: string, enabled: boolean): ng.IPromise<any> {
    const params: IUpdateSuspendedSubscriptionSettingParams = {
      user,
      enabled
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateSuspendedSubscriptionSettingRequest(params));
  }

  export function getSuspendedSubscriptionSetting(user: string): ng.IPromise<any> {
    const params: IGetSuspendedSubscriptionSettingParams = {
      user
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetSuspendedSubscriptionSettingRequest(params));
  }

  export function updateSuspendedDataAlertSetting(user: string, enabled: boolean): ng.IPromise<any> {
    const params: IUpdateSuspendedDataAlertSettingParams = {
      user,
      enabled
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateSuspendedDataAlertSettingRequest(params));
  }

  export function getSuspendedDataAlertSetting(user: string): ng.IPromise<any> {
    const params: IGetSuspendedDataAlertSettingParams = {
      user
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetSuspendedDataAlertSettingRequest(params));
  }

  export function updateMarkAnimationSetting(user: string, enabled: boolean): ng.IPromise<any> {
    const params: IUpdateMarkAnimationSettingParams = {
      user,
      enabled
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateMarkAnimationSettingRequest(params));
  }

  export function getMarkAnimationSetting(user: string): ng.IPromise<any> {
    const params: IGetMarkAnimationSettingParams = {
      user
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetMarkAnimationSettingRequest(params));
  }

  export type UserSettings = typeof UserSettings;
}


